import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import Icon from './Icon';
import useToastStore from '../../store/toastStore';
import { statusLabel } from '../../utils/format';
import { errorMessage } from '../../api/errors';
import { IsoIllustration } from '../iso/IsoSvg';

export { Icon };

/* ── Button ──────────────────────────────────────────────────────── */
export function Button({
  variant = 'p',
  size,
  loading = false,
  icon,
  iconRight,
  to,
  href,
  block,
  className = '',
  children,
  type = 'button',
  disabled,
  ...rest
}) {
  const cls = [
    'btn',
    `btn-${variant}`,
    size ? `btn-${size}` : '',
    block ? 'btn-block' : '',
    className
  ]
    .filter(Boolean)
    .join(' ');
  const inner = (
    <>
      {loading ? <span className="spin" /> : icon ? <Icon name={icon} /> : null}
      {children}
      {iconRight && !loading ? <Icon name={iconRight} /> : null}
    </>
  );
  if (to) {
    return (
      <Link to={to} className={cls} aria-disabled={disabled || loading} {...rest}>
        {inner}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} className={cls} {...rest}>
        {inner}
      </a>
    );
  }
  return (
    <button type={type} className={cls} disabled={disabled || loading} {...rest}>
      {inner}
    </button>
  );
}

/* ── Field ───────────────────────────────────────────────────────── */
export function Field({ label, required, error, hint, htmlFor, right, className = '', children }) {
  return (
    <div className={`field ${error ? 'has-error' : ''} ${className}`}>
      {label && (
        <label htmlFor={htmlFor} className="label">
          <span>
            {label}
            {required && <span className="req">*</span>}
          </span>
          {right}
        </label>
      )}
      {children}
      {error ? (
        <span className="error">
          <Icon name="alert" size={13} />
          {error}
        </span>
      ) : hint ? (
        <span className="hint">{hint}</span>
      ) : null}
    </div>
  );
}

/* ── Checkbox ────────────────────────────────────────────────────── */
export function Checkbox({ checked, onChange, error, children, ...rest }) {
  return (
    <label className={`check ${error ? 'has-error' : ''}`}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} {...rest} />
      <span className="box">
        <Icon name="check" strokeWidth={3} />
      </span>
      <span>{children}</span>
    </label>
  );
}

/* ── StatusBadge ─────────────────────────────────────────────────── */
export function StatusBadge({ status, className = '' }) {
  return (
    <span className={`badge ${className}`} data-status={status}>
      <span className="dot" />
      {statusLabel(status)}
    </span>
  );
}

/* ── Modal ───────────────────────────────────────────────────────── */
export function Modal({ open, onClose, title, sub, size, footer, children, closeOnBackdrop = true }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = e => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div
      className="modal-bg"
      onMouseDown={e => {
        if (closeOnBackdrop && e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className={`modal ${size ? `modal--${size}` : ''}`} role="dialog" aria-modal="true" ref={ref}>
        {(title || onClose) && (
          <div className="modal-head">
            <div>
              {title && <h3>{title}</h3>}
              {sub && <p>{sub}</p>}
            </div>
            {onClose && (
              <button type="button" className="btn-icon" onClick={onClose} aria-label="Close">
                <Icon name="x" />
              </button>
            )}
          </div>
        )}
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

/* ── Toaster ─────────────────────────────────────────────────────── */
export function Toaster() {
  const toasts = useToastStore(s => s.toasts);
  const remove = useToastStore(s => s.remove);
  if (toasts.length === 0) return null;
  const iconOf = { ok: 'checkCircle', err: 'alert', info: 'info' };
  return createPortal(
    <div className="toasts" aria-live="polite">
      {toasts.map(t => (
        <div key={t.id} className={`toast toast--${t.type}`}>
          <Icon name={iconOf[t.type] || 'info'} />
          <div>
            {t.title && <b>{t.title}</b>}
            {t.message && <span>{t.message}</span>}
          </div>
          <button type="button" className="x" onClick={() => remove(t.id)} aria-label="Dismiss">
            <Icon name="x" size={14} />
          </button>
        </div>
      ))}
    </div>,
    document.body
  );
}

/* ── EmptyState ──────────────────────────────────────────────────── */
export function EmptyState({ art = 'empty', title, text, action, className = '' }) {
  return (
    <div className={`empty ${className}`}>
      <div className="art">
        <IsoIllustration scene={art} />
      </div>
      {title && <h4>{title}</h4>}
      {text && <p>{text}</p>}
      {action}
    </div>
  );
}

/* ── Skeleton ────────────────────────────────────────────────────── */
export function Skeleton({ w = '100%', h = 14, r, style, className = '' }) {
  return <div className={`skel ${className}`} style={{ width: w, height: h, borderRadius: r, ...style }} />;
}

export function SkeletonRows({ rows = 4, cols = 4 }) {
  return (
    <div className="col" style={{ gap: 10 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="row" style={{ gap: 14 }}>
          {Array.from({ length: cols }).map((__, j) => (
            <Skeleton key={j} h={16} w={`${[30, 18, 14, 22, 16][j % 5]}%`} />
          ))}
        </div>
      ))}
    </div>
  );
}

/* ── Pager ───────────────────────────────────────────────────────── */
export function Pager({ page, totalPages, total, size, onChange, label = 'items' }) {
  if (!totalPages || totalPages < 1) return null;
  const from = total === 0 ? 0 : (page - 1) * size + 1;
  const to = Math.min(page * size, total);
  const nums = [];
  const lo = Math.max(1, page - 2);
  const hi = Math.min(totalPages, lo + 4);
  for (let i = lo; i <= hi; i++) nums.push(i);
  return (
    <div className="pager">
      <div className="info">
        Showing <b className="num">{from}–{to}</b> of <b className="num">{total}</b> {label}
      </div>
      <div className="pages">
        <button type="button" onClick={() => onChange(1)} disabled={page === 1} aria-label="First page">
          <Icon name="chevronsLeft" />
        </button>
        <button type="button" onClick={() => onChange(page - 1)} disabled={page === 1} aria-label="Previous page">
          <Icon name="chevronLeft" />
        </button>
        {nums.map(n => (
          <button type="button" key={n} className={n === page ? 'is-on' : ''} onClick={() => onChange(n)}>
            {n}
          </button>
        ))}
        <button type="button" onClick={() => onChange(page + 1)} disabled={page === totalPages} aria-label="Next page">
          <Icon name="chevronRight" />
        </button>
        <button type="button" onClick={() => onChange(totalPages)} disabled={page === totalPages} aria-label="Last page">
          <Icon name="chevronsRight" />
        </button>
      </div>
    </div>
  );
}

/* ── PageHead ────────────────────────────────────────────────────── */
export function PageHead({ crumb = [], title, lede, right, children }) {
  return (
    <div className="page-head">
      <div className={`wrap ${right ? 'page-head--row' : ''}`}>
        <div>
          {crumb.length > 0 && (
            <div className="crumb">
              {crumb.map((c, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="sep">/</span>}
                  {c.to ? <Link to={c.to}>{c.label}</Link> : <span>{c.label}</span>}
                </React.Fragment>
              ))}
            </div>
          )}
          <h1>{title}</h1>
          {lede && <p className="lede">{lede}</p>}
          {children}
        </div>
        {right && <div>{right}</div>}
      </div>
    </div>
  );
}

/* ── Tabs ────────────────────────────────────────────────────────── */
export function Tabs({ items, value, onChange, className = '' }) {
  return (
    <div className={`tabs ${className}`} role="tablist">
      {items.map(t => (
        <button
          type="button"
          key={t.key}
          role="tab"
          aria-selected={value === t.key}
          className={value === t.key ? 'is-on' : ''}
          onClick={() => onChange(t.key)}
        >
          {t.label}
          {t.count !== undefined && t.count !== null && <span className="count">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

/* ── Note ────────────────────────────────────────────────────────── */
export function Note({ kind = 'info', title, children, className = '' }) {
  const icon = { info: 'info', ok: 'checkCircle', warn: 'alertTri', err: 'alert', mute: 'info' }[kind];
  return (
    <div className={`note note--${kind} ${className}`}>
      <Icon name={icon} />
      <div>
        {title && <b>{title}</b>}
        {children}
      </div>
    </div>
  );
}

/* ── Avatar ──────────────────────────────────────────────────────── */
export function Avatar({ user, size, className = '' }) {
  const name = user?.name || 'User';
  const init = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(s => s[0]?.toUpperCase() || '')
    .join('');
  return (
    <span className={`avatar ${size === 'lg' ? 'avatar--lg' : ''} ${className}`} aria-hidden="true">
      {user?.thumbnailUrl ? <img src={user.thumbnailUrl} alt="" /> : init || 'U'}
    </span>
  );
}

/* ── ErrorState ──────────────────────────────────────────────────── */
/** 목록·대시보드 쿼리 실패. 스켈레톤이 영원히 돌지 않게 원인과 재시도를 보여준다. */
export function ErrorState({ title = 'Couldn’t load this', error, onRetry, className = '', style }) {
  return (
    <div className={`error-state ${className}`} role="alert" style={style}>
      <Icon name="alertTri" size={22} />
      <div className="grow" style={{ minWidth: 0 }}>
        <b>{title}</b>
        <p>{error ? errorMessage(error) : 'Check your connection and try again.'}</p>
      </div>
      {onRetry && (
        <button type="button" className="btn btn-s btn-sm" onClick={() => onRetry()}>
          <Icon name="refresh" size={15} />
          Retry
        </button>
      )}
    </div>
  );
}
